import React, {useEffect, useState} from 'react'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import axios from 'axios'
import SurveyResultsComponent from '../../modules/surveyanalytics/surveyanalytics'
import SurveyGaugeChart from '../../modules/surveyanalytics/SurveyGaugeChart'
import ProgramOutcomeBarChart from '../../modules/surveyanalytics/ProgramOutcomeBarChart'
import SurveyTableComponent from '../../modules/surveyanalytics/surveyanalyticstable'
// Bunu import etmeyi unutma!
import SurveyOrderResultComponent from '../../modules/surveyanalytics/SurveyOrderResultComponent'
import Swal from 'sweetalert2'
import HelpVideoModal from '../../modules/utils/HelpVideoModal'
import {sanitizeChatGptResponse} from '../../modules/surveyanalytics/utils'

const API_URL = process.env.REACT_APP_API_URL
const isAiActive = process.env.REACT_APP_YZACTIVE === '1'

interface Semester {
  id: number
  name: string
}
interface Survey {
  id: number
  name: string
}
interface Organization {
  id: number
  name: string
}
interface Course {
  id: number
  name: string
  instructorName: string
  semesterCourseId?: number
}
interface SurveyOrderResult {
  rankInDepartment: number
  totalInDepartment: number
  rankInFaculty: number
  totalInFaculty: number
  rankInUniversity: number
  totalInUniversity: number
}

const CourseEvaluationResultsComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [semesters, setSemesters] = useState<Semester[]>([])
  const [selectedSemester, setSelectedSemester] = useState<number | undefined>(undefined)

  const [surveys, setSurveys] = useState<Survey[]>([])
  const [selectedSurvey, setSelectedSurvey] = useState<number | undefined>(undefined)

  const [faculties, setFaculties] = useState<Organization[]>([])
  const [selectedFaculty, setSelectedFaculty] = useState<number | undefined>(undefined)

  const [departments, setDepartments] = useState<Organization[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<number | undefined>(undefined)

  const [courses, setCourses] = useState<Course[]>([])
  const [selectedCourse, setSelectedCourse] = useState<number | undefined>(undefined)

  const [survey, setSurvey] = useState<any>(null)
  const [surveyResults, setSurveyResults] = useState<any[]>([])
  const [surveyEndAt, setSurveyEndAt] = useState<string | null>(null)
  const [uniqueParticipantCount, setUniqueParticipantCount] = useState<number>(0)
  const [orderResult, setOrderResult] = useState<SurveyOrderResult | null>(null)

  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [loadingAI, setLoadingAI] = useState(false)

  const [feedback, setFeedback] = useState<string | null>(null)
  const [loadingFeedback, setLoadingFeedback] = useState(false)

  const [loadingData, setLoadingData] = useState(false)

  const [pcNames, setPcNames] = useState([])
  const [pcAverages, setPcAverages] = useState([])

  const formatSurveyDate = (value?: string | null) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    return date.toLocaleDateString('tr-TR')
  }

  const [userUnitInfo, setUserUnitInfo] = useState<{
    isRector: boolean
    isDean: boolean
    isHeadOfDepartment: boolean
    facultyId?: number
    departmentId?: number
  } | null>(null)

  useEffect(() => {
    axios.get(`${API_URL}/Custom/GetCurrentUserUnitInfo`).then((res) => {
      // store role-based unit access for later selections
      setUserUnitInfo(res.data)

      // Dekan veya Bölüm Başkanı ise kendi fakülte/bölümünü sabitle
      if ((res.data.isDean || res.data.isHeadOfDepartment) && res.data.facultyId) {
        setSelectedFaculty(res.data.facultyId)
      }
      if (res.data.isHeadOfDepartment && res.data.departmentId) {
        setSelectedDepartment(res.data.departmentId)
      }
    })
  }, [])

  useEffect(() => {
    setFeedback(null)
    if (selectedCourse && selectedSurvey) {
      setLoadingFeedback(true)
      axios
        .get(`${API_URL}/Custom/GetReadonlySurveyFeedback?semesterCourseId=${selectedCourse}`)
        .then((res) => setFeedback(res.data?.feedback ?? null))
        .catch(() => setFeedback(null))
        .finally(() => setLoadingFeedback(false))
    }
  }, [selectedCourse, selectedSurvey])

  // Dönemleri çek
  useEffect(() => {
    axios.get(`${API_URL}/Custom/ListPublishedSemesters`).then((res) => setSemesters(res.data))
  }, [])

  // Dönem seçilince: Survey listesi çek
  useEffect(() => {
    if (selectedSemester) {
      axios
        .get(`${API_URL}/Custom/ListCourseEvaluationSurveys?semesterId=${selectedSemester}`)
        .then((res) => setSurveys(res.data))
      setSelectedSurvey(undefined)
      setFaculties([])
      setSelectedFaculty(undefined)
      setDepartments([])
      setSelectedDepartment(undefined)
      setCourses([])
      setSelectedCourse(undefined)
      setSurvey(null)
      setSurveyResults([])
      setOrderResult(null)
    }
  }, [selectedSemester])

  // Survey seçilince: Fakülteleri çek
  useEffect(() => {
    if (selectedSurvey) {
      axios.get(`${API_URL}/Custom/GetAccessibleFaculties`).then((res) => {
        setFaculties(res.data)
        if ((userUnitInfo?.isDean || userUnitInfo?.isHeadOfDepartment) && userUnitInfo?.facultyId) {
          setSelectedFaculty(userUnitInfo.facultyId)
        } else {
          setSelectedFaculty(undefined)
        }
      })
      setDepartments([])
      setSelectedDepartment(undefined)
      setCourses([])
      setSelectedCourse(undefined)
      setSurvey(null)
      setSurveyResults([])
      setOrderResult(null)
    }
  }, [selectedSurvey, userUnitInfo])

  // Bölümleri çek
  useEffect(() => {
    if (selectedFaculty) {
      axios.get(`${API_URL}/Custom/GetDepartmentsByFaculty/${selectedFaculty}`).then((res) => {
        setDepartments(res.data)
        if (userUnitInfo?.isHeadOfDepartment && userUnitInfo?.departmentId) {
          setSelectedDepartment(userUnitInfo.departmentId)
        } else {
          setSelectedDepartment(undefined)
        }
        setSelectedCourse(undefined)
        setSurvey(null)
        setSurveyResults([])
        setOrderResult(null)
      })
    }
  }, [selectedFaculty, userUnitInfo]) // geri al: dekan/rektör akışını bozmasın

  // Bölüm başkanları için bölümü otomatik seç
  useEffect(() => {
    if (userUnitInfo?.isHeadOfDepartment && !userUnitInfo.isDean && userUnitInfo.departmentId) {
      setSelectedDepartment(userUnitInfo.departmentId)
    }
  }, [userUnitInfo, departments])

  // Dersleri çek
  useEffect(() => {
    if (selectedDepartment !== undefined && selectedSemester !== undefined) {
      axios
        .get(
          `${API_URL}/Custom/GetCoursesByDepartment?departmentId=${selectedDepartment}&semesterId=${selectedSemester}`
        )
        .then((res) => setCourses(res.data))
      setSelectedCourse(undefined)
      setSurvey(null)
      setSurveyResults([])
      setOrderResult(null)
    }
  }, [selectedDepartment, selectedSemester]) // geri al: çift tetiklenmeyi engelle

  // 🔔 HoD için: Anket seçildiğinde (bölüm select kilitli olsa bile) dersleri getir
  useEffect(() => {
    if (
      selectedSurvey &&
      selectedSemester &&
      userUnitInfo?.isHeadOfDepartment &&
      userUnitInfo.departmentId &&
      selectedDepartment == null // bölüm select'i kilitli/atanmamışsa yalnızca burada tetikle
    ) {
      axios
        .get(
          `${API_URL}/Custom/GetCoursesByDepartment?departmentId=${userUnitInfo.departmentId}&semesterId=${selectedSemester}`
        )
        .then((res) => setCourses(res.data))
        .catch(() => setCourses([]))
    }
  }, [selectedSurvey, selectedSemester, userUnitInfo, selectedDepartment])

  useEffect(() => {
    setSurvey(null)
    setSurveyResults([])
    setSurveyEndAt(null)
    setUniqueParticipantCount(0)
    setOrderResult(null)

    const courseId = selectedCourse

    if (selectedSurvey !== undefined && selectedSemester !== undefined) {
      setLoadingData(true)
      let url = `${API_URL}/Custom/CourseEvaluationRawData?surveyId=${selectedSurvey}&semesterId=${selectedSemester}`
      if (selectedFaculty) url += `&facultyId=${selectedFaculty}`
      if (selectedDepartment) url += `&departmentId=${selectedDepartment}`
      if (courseId) url += `&courseId=${courseId}`

      axios
        .get(url)
        .then((res) => {
          if (courseId !== selectedCourse) return
          /* ---------- 0) Response, Survey ---------------------------- */
          let surveyResults = res.data.responses.map((r: any) => {
            const obj = JSON.parse(r.name)
            obj.surveyApplicationId = r.surveyApplicationId
            return obj
          })
          let newSurveyJson = JSON.parse(res.data.surveyJson)

          setPcNames(res.data.programOutcomeNames ?? [])
          setPcAverages(res.data.programOutcomeAverages ?? [])
          const participantCount =
            typeof res.data.uniqueParticipantCount === 'number'
              ? res.data.uniqueParticipantCount
              : surveyResults.length

          /* ---------- 2) COURSE OUTCOME (OC_) ------------------------- */
          if (courseId) {
            const ocPoints = res.data.courseOutcomes ?? []
            const ocNames = res.data.courseOutcomeNames ?? []

            if (ocPoints.length && ocNames.length) {
              /* 2a) Puanları satırlara yaz */
              const byApp: Record<number, any[]> = {}
              ocPoints.forEach((co: any) => {
                ;(byApp[co.surveyApplicationId] ??= []).push(co)
              })

              surveyResults = surveyResults.map((sr: any) => {
                byApp[sr.surveyApplicationId]?.forEach((o) => {
                  sr['oc_' + o.courseOutcomeId] = o.point
                })
                return sr
              })

              /* 2b) Rating soruları ekle */
              const ratingElems = ocNames.map((co: any) => ({
                type: 'rating',
                name: 'oc_' + co.id,
                title: co.name,
                rateCount: 10,
                rateMax: 10,
              }))
              if (newSurveyJson.pages?.length) newSurveyJson.pages[0].elements.push(...ratingElems)
            } else {
              /* Öğrenme çıktısı tanımlı değil → kullanıcıya bilgilendirme */
              Swal.fire({
                icon: 'info',
                title: 'Ders Öğrenme Çıktıları Tanımlanmamış',
                width: '60em',
                html: `
        Bu derse ait <strong>öğrenme çıktıları</strong> bilgi paketine
        <strong>dönem başlamadan önce</strong> girilmemiş. <br/><br/>
        Bu nedenle öğrencilere yalnızca sabit sorular sorulmuş,
        öğrenme çıktılarıyla ilgili değerlendirme yapılamamıştır.`,
                confirmButtonText: 'Tamam',
              })
            }
          }

          /* ---------- 3) State güncelle ------------------------------ */
          //console.log(surveyResults)
          //console.log(newSurveyJson)
          setSurvey(newSurveyJson)
          setSurveyEndAt(res.data.surveyEndAt || null)
          setUniqueParticipantCount(participantCount)
          setSurveyResults(surveyResults)
        })
        .catch(() => {
          setSurvey(null)
          setSurveyResults([])
          setSurveyEndAt(null)
          setUniqueParticipantCount(0)
        })
        .finally(() => setLoadingData(false))
    } else {
      setLoadingData(false)
      setSurveyEndAt(null)
      setUniqueParticipantCount(0)
    }
  }, [selectedSurvey, selectedFaculty, selectedCourse, selectedSemester])

  useEffect(() => {
    setSelectedCourse(undefined)

    setSurvey(null)
    setSurveyResults([])
    setSurveyEndAt(null)
    setUniqueParticipantCount(0)
    setOrderResult(null)

    const courseId = selectedCourse

    if (selectedSurvey !== undefined && selectedSemester !== undefined) {
      setLoadingData(true)
      let url = `${API_URL}/Custom/CourseEvaluationRawData?surveyId=${selectedSurvey}&semesterId=${selectedSemester}`
      if (selectedFaculty) url += `&facultyId=${selectedFaculty}`
      if (selectedDepartment) url += `&departmentId=${selectedDepartment}`

      axios
        .get(url)
        .then((res) => {
          if (courseId !== selectedCourse) return
          /* ---------- 0) Response, Survey ---------------------------- */
          let surveyResults = res.data.responses.map((r: any) => {
            const obj = JSON.parse(r.name)
            obj.surveyApplicationId = r.surveyApplicationId
            return obj
          })
          let newSurveyJson = JSON.parse(res.data.surveyJson)

          setPcNames(res.data.programOutcomeNames ?? [])
          setPcAverages(res.data.programOutcomeAverages ?? [])
          const participantCount =
            typeof res.data.uniqueParticipantCount === 'number'
              ? res.data.uniqueParticipantCount
              : surveyResults.length

          /* ---------- 3) State güncelle ------------------------------ */
          //console.log(surveyResults)
          //console.log(newSurveyJson)
          setSurvey(newSurveyJson)
          setSurveyEndAt(res.data.surveyEndAt || null)
          setUniqueParticipantCount(participantCount)
          setSurveyResults(surveyResults)
        })
        .catch(() => {
          setSurvey(null)
          setSurveyResults([])
          setSurveyEndAt(null)
          setUniqueParticipantCount(0)
        })
        .finally(() => setLoadingData(false))
    } else {
      setLoadingData(false)
      setSurveyEndAt(null)
      setUniqueParticipantCount(0)
    }
  }, [selectedDepartment])

  // Sıralama verisini çek (ders ve survey seçiliyse)
  useEffect(() => {
    setOrderResult(null)
    if (selectedCourse && selectedSurvey) {
      axios
        .get(
          `${API_URL}/Custom/GetSurveyAnalyticsResultWithRanking?semesterCourseId=${selectedCourse}`
        )
        .then((res) => {
          if (res.data && res.data.length > 0) setOrderResult(res.data[0])
        })
        .catch(() => setOrderResult(null))
    }
  }, [selectedCourse, selectedSurvey])

  // AI Analizi getir (modal açılınca)
  const openAIModal = () => {
    setModalOpen(true)
    setLoadingAI(true)
    setAiAnalysis(null)
    axios
      .post(`${API_URL}/Custom/RunSurveyChatGptAnalysis?semesterCourseId=${selectedCourse}`)
      .then((res) => setAiAnalysis(sanitizeChatGptResponse(res?.data?.response)))
      .catch(() => setAiAnalysis(null))
      .finally(() => setLoadingAI(false))
  }

  const convertTextToHTML = (text: string): string => {
    return text
      ? text
          .replace(/(### .*)/g, '\n$1\n')
          .replace(/(\*\*.*?\*\*):/g, '\n$1:\n')
          .split('\n')
          .map((line) => {
            line = line.trim()
            if (line.startsWith('### ')) return `<h3>${line.replace('### ', '').trim()}</h3>`
            line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            if (/^<strong>.*<\/strong>:/.test(line)) return `<p>${line}</p>`
            if (line.length > 0) return `<p>${line}</p>`
            return ''
          })
          .join('\n')
          .replace(/#/g, '')
      : ''
  }

  return (
    <div className='container'>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <button className='btn btn-light' onClick={() => navigate('/dashboard')}>
          🔙 Geri
        </button>

        <HelpVideoModal videoName='DersDegerlendirmeManagement' />
      </div>
      <h3 className='mb-4'>Ders Değerlendirme Anket Sonuçları</h3>
      {/* Filtreler */}
      <div className='card p-3 mb-4'>
        <div className='row mb-2'>
          <div className='col-md-6'>
            <label>Dönem</label>
            <select
              className='form-select'
              value={selectedSemester ?? ''}
              onChange={(e) => setSelectedSemester(Number(e.target.value) || undefined)}
            >
              <option value=''>Seçiniz</option>
              {semesters.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-6'>
            <label>Anket</label>
            <select
              className='form-select'
              value={selectedSurvey ?? ''}
              onChange={(e) => setSelectedSurvey(Number(e.target.value) || undefined)}
              disabled={!selectedSemester}
            >
              <option value=''>Seçiniz</option>
              {surveys.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className='row mb-2'>
          <div className='col-md-4'>
            <label>Fakülte / Yüksekokul / Enstitü</label>
            <select
              className='form-select'
              value={selectedFaculty ?? ''}
              onChange={(e) => setSelectedFaculty(Number(e.target.value) || undefined)}
              disabled={!selectedSurvey || userUnitInfo?.isDean || userUnitInfo?.isHeadOfDepartment}
            >
              <option value=''>Tümü</option>
              {faculties.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-4'>
            <label>Bölüm / Anabilim Dalı</label>
            <select
              className='form-select'
              value={selectedDepartment ?? ''}
              onChange={(e) => setSelectedDepartment(Number(e.target.value) || undefined)}
              disabled={
                !selectedFaculty || (userUnitInfo?.isHeadOfDepartment && !userUnitInfo?.isDean)
              }
            >
              <option value=''>Tümü</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-4'>
            <label>Ders</label>
            <select
              className='form-select'
              value={selectedCourse ?? ''}
              onChange={(e) => setSelectedCourse(Number(e.target.value) || undefined)}
              disabled={!selectedDepartment}
            >
              <option value=''>Tümü</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.instructorName})
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* Yapay Zeka Analizi butonu: Sadece ders seçiliyse */}
        {isAiActive && selectedCourse && selectedSurvey && (
          <button className='btn btn-primary mt-3' onClick={openAIModal}>
            🧠 Yapay Zeka Analizi / Öğretim Elemanı Raporu
          </button>
        )}
      </div>

      {/* Sıralama alanı */}
      {selectedCourse && selectedSurvey && orderResult && (
        <div className='mb-4'>
          <SurveyOrderResultComponent result={orderResult} />
        </div>
      )}

      {/* AI Analiz Modalı */}
      {isAiActive && modalOpen && (
        <div className='modal show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-xl modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Yapay Zeka Analizi & Öğretim Elemanı Raporu</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setModalOpen(false)}
                ></button>
              </div>
              <div className='modal-body'>
                {loadingAI ? (
                  <p>Yapay zeka analizi hazırlanıyor. Lütfen bekleyiniz...</p>
                ) : aiAnalysis ? (
                  <div dangerouslySetInnerHTML={{__html: convertTextToHTML(aiAnalysis)}} />
                ) : (
                  <p>Analiz mevcut değil veya yapılamadı.</p>
                )}
                <hr />
                <b>Öğretim Elemanı İyileştirme Raporu:</b>
                <div className='border rounded p-3 mt-2' style={{background: '#f8f9fa'}}>
                  {loadingFeedback ? (
                    <span>Yükleniyor...</span>
                  ) : feedback ? (
                    <div dangerouslySetInnerHTML={{__html: feedback}} />
                  ) : (
                    <span className='text-muted'>Henüz rapor bulunamadı.</span>
                  )}
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setModalOpen(false)}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sonuçlar */}
      {loadingData ? (
        <div
          className='d-flex justify-content-center align-items-center my-5'
          style={{minHeight: 200}}
        >
          <div className='spinner-border text-primary me-2' role='status' />
          <span>Veriler yükleniyor...</span>
        </div>
      ) : survey && surveyResults.length > 0 ? (
        <>
          <div className='card p-3 mb-4'>
            <h5 className='mb-3'>Anket Sonuç Raporu</h5>
            <div className='d-flex flex-wrap gap-4'>
              <div>
                <strong>Anket Tarihi:</strong> {formatSurveyDate(surveyEndAt)}
              </div>
              <div>
                <strong>Tekil Katılımcı Sayısı:</strong> {uniqueParticipantCount}
              </div>
            </div>
          </div>
          <SurveyGaugeChart surveyJson={survey} data={surveyResults} />
          {!!selectedDepartment && !selectedCourse && pcAverages.length > 0 && (
            <div className='card p-3 mb-4'>
              <h5 className='mb-3'>Program Çıktıları Sağlanma Düzeyi</h5>
              <ProgramOutcomeBarChart names={pcNames} averages={pcAverages} />
            </div>
          )}
          <SurveyResultsComponent surveyJson={survey} surveyData={surveyResults} />
          <SurveyTableComponent surveyJson={survey} surveyData={surveyResults} />
        </>
      ) : (
        selectedCourse &&
        selectedSurvey && <div className='alert alert-warning mt-3'>Sonuç bulunamadı.</div>
      )}
    </div>
  )
}

export default CourseEvaluationResultsComponent
